import React from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { EmptyNote } from '../../../components/technician/ui/EmptyNote';
import { KEYPAD_ADDRESS_MODE, KEYPAD_FAMILY_SPECS } from '../catalog/family';
import { KEYPAD_TYPE_SPECS } from '../catalog/type';
import { DEVICE_ANSWER } from '../catalog/status';
import { familyOf, programmedKeyCount, sortedKeypads } from '../model';
import type { Keypad, KeypadBook } from '../model';
import { overallStatus } from '../stored';
import type { KeyStateReport } from '../stored';
import { countOf } from './count';
import { StatusChip } from './StatusChip';

/** What the list needs to know about a panel, without re-deriving it here. */
export interface PanelSummary {
  readonly keypad: Keypad;
  readonly reports: readonly KeyStateReport[];
  /** States that would be written right now. */
  readonly states: number;
  /** States that cannot be written as they stand. */
  readonly blocking: number;
}

export interface KeypadListProps {
  readonly book: KeypadBook;
  readonly summaries: readonly PanelSummary[];
  readonly selectedId: string | null;
  readonly onSelect: (keypad: Keypad) => void;
  readonly onAdd: () => void;
}

/** How a panel's address reads in a list row. */
const addressLabel = (keypad: Keypad): string => {
  const spec = KEYPAD_FAMILY_SPECS[familyOf(keypad)];
  if (spec.addressMode === KEYPAD_ADDRESS_MODE.FIXED) return 'Fixed address';
  return keypad.address === null ? 'No address' : `Address ${keypad.address}`;
};

/**
 * Every panel in the book, in address order.
 *
 * Address order rather than name order because that is how the fitter's schedule
 * and the RCU's own mapping table read, and because it keeps a panel in the same
 * place on screen as its address changes hands.
 *
 * The row carries the three things a technician looks for: what the panel is,
 * how much of it is programmed, and whether the device agrees. The dot on the
 * canvas answers the third question for one key; this answers it for the panel,
 * so a building with twenty panels can be read at a glance.
 */
export const KeypadList: React.FC<KeypadListProps> = ({
  book,
  summaries,
  selectedId,
  onSelect,
  onAdd,
}) => {
  const panels = sortedKeypads(book);

  if (panels.length === 0) {
    return (
      <EmptyNote
        message="No panels yet. Add the keypads and the keycard reader fitted to this RCU, then map what their keys do."
        action={
          <Button variant="primary" onClick={onAdd} icon={<Plus className="h-4 w-4" />}>
            Add a panel
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-2">
      {panels.map((keypad) => {
        const summary = summaries.find((entry) => entry.keypad.id === keypad.id);
        const reports = summary?.reports ?? [];
        const status = overallStatus(reports);
        // Whether this panel has been asked about at all, rather than which of
        // its key states answered: an empty state is not waiting for anything.
        const readBack = reports.some(
          (report) => report.answer.kind !== DEVICE_ANSWER.UNKNOWN,
        );
        const states = summary?.states ?? 0;
        const isSelected = keypad.id === selectedId;

        return (
          <button
            key={keypad.id}
            type="button"
            onClick={() => onSelect(keypad)}
            className={`w-full rounded-xl border p-4 text-left transition-colors ${
              isSelected ? 'border-[#D4A054] bg-[#FFFBF4]' : 'border-[#E0E0E0] hover:border-[#D4A054]/60'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#333333]">
                  {keypad.name.trim() === '' ? 'Unnamed panel' : keypad.name}
                </p>
                <p className="text-xs text-[#666666]">
                  {KEYPAD_TYPE_SPECS[keypad.type].label} · {addressLabel(keypad)}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                {status !== null && <StatusChip status={status} />}
                {(summary?.blocking ?? 0) > 0 && (
                  <span className="text-xs font-medium text-[#C0392B]">
                    {countOf(summary?.blocking ?? 0, 'key state', 'key states')} cannot be
                    written
                  </span>
                )}
              </div>
            </div>
            <p className="mt-2 text-xs text-[#666666]">
              {programmedKeyCount(keypad)} of {keypad.keys.length} keys programmed ·{' '}
              {countOf(states, 'state', 'states')} to write
              {readBack ? '' : ' · not read back yet'}
            </p>
          </button>
        );
      })}
    </div>
  );
};

export default KeypadList;
